import { animate, actionSetOA2T, actionOA2T } from '@kit/utils/Animate'

/* ** ** ** ** ** ** ** ** ** ** ** ** *\

Follows the same pattern as the scroller.

Ex 1: Make the manager
stb = new SimpleTabBehavior()

Ex 2: Register a new element with explicit options for the #my-host-element selector
Note that if there's multiple such elements it will create a simple tab behavior for all of them.

stb.register('#my-host-element' {
          tabIdPfx: "w-tabs-0-data-w-tab-", 
          paneIdPfx: "w-tabs-0-data-w-pane-",
          flashSpreadId: "flash-spread",
          tabSelectClasses: "tab-link w-inline-block w-tab-link w--current",
          tabUnselectClasses: "tab-link w-inline-block w-tab-link",
          paneSelectClasses: "w-tab-pane w--tab-active",
          paneUnselectClasses: "w-tab-pane",
          startWith: 0, 
          len: 3
})

Ex 3: Register a new element without explicit options, but the exact same object 
as above will be expected to exist as a json string on the attribute "data-fx-simple-tab-behavior"

stb.register('#my-host-element')

Ex 4: Destroy:

stb.destroy()

And it will destroy all of the registered behavior-sets.


{ "tabIdPfx": "w-tabs-0-data-w-tab-", "paneIdPfx": "w-tabs-0-data-w-pane-", "flashSpreadId": "flash-spread", "tabSelectClasses": "tab-link w-inline-block w-tab-link w--current", "tabUnselectClasses": "tab-link w-inline-block w-tab-link", "paneSelectClasses": "w-tab-pane w--tab-active", "paneUnselectClasses": "w-tab-pane", "len": 3 }

\* ** ** ** ** ** ** ** ** ** ** ** ** */

  const tabActions = actionSetOA2T([
    {
      name:"tabClick",
      async action(newTab, spreader, prevTab, ctx, snap) {
        spreader.style.display = "inline"
        spreader.style.opacity = 0;
        const duration = snap ? 0 : 100
  
        await animate({
          targets:spreader,
          opacity:1,
          duration,
          easing:"linear"
        })  
    
        if(prevTab) {
          ctx.unselectPane(prevTab)
        }
        ctx.selectPane(newTab)

        await animate({
          targets:spreader,
          opacity:0,
          duration,
          easing:"linear"
        })

        spreader.style.display = "none"
    
      }
    }
  ])

  class SimpleTabBehaviorInst {

    #previousTab = null
    #flashSpread = null
    #tabs = null
    #tabSelectClasses = null
    #tabUnselectClasses = null
    #paneSelectClasses = null
    #paneUnselectClasses = null
    #startWith = 0
    #startWithTab = null

    constructor(
    hostElement, 
    {
      tabIdPfx, 
      paneIdPfx, 
      flashSpreadId, 
      tabSelectClasses,
      tabUnselectClasses,
      paneSelectClasses,
      paneUnselectClasses,
      startWith,
      len,
    }) {
      this.#tabs = []

      this.#tabSelectClasses = tabSelectClasses
      this.#tabUnselectClasses = tabUnselectClasses
      this.#paneSelectClasses = paneSelectClasses
      this.#paneUnselectClasses = paneUnselectClasses
      this.#startWith = startWith || 0

      this.#flashSpread = hostElement.querySelector(`#${flashSpreadId}`)

      this.setupTabs(hostElement, tabIdPfx, paneIdPfx, len)
      
    }

    destroy() {
      this.teardownTabs()
    }

    getPaneFromTab(tab) {
      return tab["__pane"]
    }

    setupTab(hostElement, tabIdPfx, paneIdPfx, idx) {
      const tab = hostElement.querySelector(`#${tabIdPfx}${idx}`)
      
      if(!tab) {
        throw new Error("No tab for id: "+`${tabIdPfx}${idx}`)
      }
      const pane = hostElement.querySelector(`#${paneIdPfx}${idx}`)
      
      if(!pane) {
        throw new Error("No tab for id: "+`${paneIdPfx}${idx}`)
      }

      tab["__pane"] = pane
      tab.addEventListener("click", (e) => { 
        this.tabClicked(e) 
      })
      this.#tabs.push(tab)
    }

    teardownTab(tab) {
      tab.removeEventListener("click", (e) => { 
        this.tabClicked(e) 
      })
    }

    setupTabs(hostElement, tabIdPfx, paneIdPfx, len) {
      for(let i=0; i<len; i++) {
        this.setupTab(hostElement, tabIdPfx, paneIdPfx, i)
      }

      if(this.#startWith || this.#startWith === 0) {
        this.tabClickDynamic(this.#startWith)
      } else {
        this.#previousTab = this.#tabs[0]
      }

    }

    teardownTabs() {
      const tabs = this.#tabs
      for(let i=0; i<tabs.length; i++) {
        const tab = tabs[i]
        this.teardownTab(tab)
      }
      this.#tabs = null;
    }

    selectTab(tab) {
      tab.setAttribute("aria-selected","true")
      tab.setAttribute("class", this.#tabSelectClasses)
    }

    selectPane(tab) {
      tab.setAttribute("aria-selected","false")
      const pane = this.getPaneFromTab(tab)
      pane.setAttribute("class",this.#paneSelectClasses)
    }

    unselectTab(tab) {
      tab.setAttribute("class", this.#tabUnselectClasses)
    }

    unselectPane(tab) {
      const pane = this.getPaneFromTab(tab)
      pane.setAttribute("class", this.#paneUnselectClasses)
    }

    tabClickDynamic(whichTab) {
      if(this.#previousTab) {
        this.unselectTab(this.#previousTab)
      }
      if(whichTab >= 0 && whichTab < this.#tabs.length) {
        const tab = this.#tabs[whichTab]
        this.selectTab(tab)
        actionOA2T(tabActions, "tabClick", [tab, this.#flashSpread, this.#previousTab, this, true])
        this.#previousTab = tab
      }
    }

    tabClicked(e) {
      e.preventDefault()
      const { currentTarget } = e

      if(this.#previousTab) {
        this.unselectTab(this.#previousTab)
      }
      this.selectTab(currentTarget)

      actionOA2T(tabActions, "tabClick", [currentTarget, this.#flashSpread, this.#previousTab, this, false])

      this.#previousTab = currentTarget
    }

  
  }

  class SimpleTabBehavior {

    #instances = null
    static inBrowser = typeof window !== 'undefined'

    constructor() {
      this.destroyed = false
      this.#instances = []
    }

    destroy() {
      this.destroyed = true
      for(let i=0; i<this.#instances.length; i++) {
        const inst = this.#instances[i]
        inst.destroy()
      }
      this.#instances = null
    }

    register(querySelectorOrNode, fxOptionsObj) {
      if(this.destroyed) {
        return
      }
      if (SimpleTabBehavior.inBrowser) {
 
        let whichOnes = typeof querySelectorOrNode == "string" 
          //if string, then we'll treat it as a query selector
          ? document.querySelectorAll(querySelectorOrNode)
          //else, treat it as a node
          : [querySelectorOrNode]
  
        for (let i=0; i<whichOnes.length; i++) {
  
          const hostElement = whichOnes[i]   
                
      
          const fxDataString = hostElement.getAttribute("data-fx-simple-tab-behavior")
          let fxInfo = null 
  
          try {
            if(fxDataString) {
              fxInfo = JSON.parse(fxDataString)
            } else 
            if(fxOptionsObj) {
              fxInfo = { ...fxOptionsObj }
            }
          } catch(err) {
            //swallow the error
          }
  
          if (!fxInfo) {
            throw new Error(
              "SimpleteTabBehavior: Malformed animation data for this element: " + fxDataString + " here's the querySelectorOrNode: " + querySelectorOrNode
            )
          }
      
          //Create a new instance and append it to the array
          const inst = new SimpleTabBehaviorInst(hostElement, fxInfo)

          this.#instances.push(inst)


        }
        
      }
    }


  }


  export default SimpleTabBehavior